import { useState } from 'react';

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  imgClassName?: string;
  width?: number;
  height?: number;
  loading?: 'lazy' | 'eager';
  /** Improves LCP when set to `high` on the hero image. */
  fetchPriority?: 'high' | 'low' | 'auto';
}

export const OptimizedImage = ({
  src,
  alt,
  className = '',
  imgClassName = '',
  width,
  height,
  loading = 'lazy',
  fetchPriority,
}: OptimizedImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Optimiser les images pour les performances
  const optimizeSrc = (originalSrc: string) => {
    // Optimiser les images Wikimedia/Wikipedia
    if (originalSrc.includes('wikimedia.org') || originalSrc.includes('wikipedia.org')) {
      return originalSrc.replace(/\/(\d+)px-/, '/600px-'); // Increased from 400px to 600px
    }
    
    // Optimiser les images Unsplash
    if (originalSrc.includes('unsplash.com')) {
      const url = new URL(originalSrc);
      url.searchParams.set('q', '75'); // Increased quality from 60 to 75
      url.searchParams.set('w', Math.min(width || 800, 1200).toString()); // Better responsive sizing
      url.searchParams.set('fm', 'webp'); // Format WebP plus efficace
      url.searchParams.set('auto', 'format,compress'); // Optimisation auto
      return url.toString();
    }
    
    // Optimiser les autres images externes
    if (originalSrc.startsWith('http') && !originalSrc.includes(window.location.hostname)) {
      try {
        const url = new URL(originalSrc);
        // Ajouter des paramètres d'optimisation génériques si possible
        if (url.pathname.includes('.jpg') || url.pathname.includes('.jpeg')) {
          // Pour les images JPEG, essayer d'ajouter des paramètres d'optimisation
          if (!url.searchParams.has('w') && width) {
            url.searchParams.set('w', Math.min(width, 1200).toString());
          }
          if (!url.searchParams.has('q') && !url.searchParams.has('quality')) {
            url.searchParams.set('q', '75'); // Better quality
          }
        }
        return url.toString();
      } catch {
        return originalSrc;
      }
    }
    
    return originalSrc;
  };

  const optimizedSrc = optimizeSrc(src);

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {!isLoaded && !hasError && (
        <div 
          className="absolute inset-0 bg-muted/20 animate-pulse"
          style={{ width, height }}
        />
      )}
      
      <img
        src={optimizedSrc}
        alt={alt}
        width={width}
        height={height}
        loading={loading}
        decoding="async"
        fetchPriority={fetchPriority}
        className={`w-full h-full object-cover object-center transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'} ${hasError ? 'hidden' : ''} ${imgClassName}`}
        onLoad={() => setIsLoaded(true)}
        onError={() => setHasError(true)}
      />
      
      {hasError && (
        <div 
          className="flex items-center justify-center bg-muted/10 text-muted-foreground"
          style={{ width, height }}
        >
          Image non disponible
        </div>
      )}
    </div>
  );
};